export const PARTICIPANT_DIRECTORY_PAGE_SIZE = 12;

export type DirectoryGroupMeta = {
  hasNextPage: boolean;
  nextCursor: string | null;
};

export type DirectoryRelationKey = "participants" | "exhibitors" | "sponsors";

export function directoryRelationKey(type: string): DirectoryRelationKey {
  if (type === "EXHIBITORS") return "exhibitors";
  if (type === "SPONSORS") return "sponsors";
  return "participants";
}

/** take+1 window: extra row means another page; nextCursor is last kept id. */
export function directoryPageMeta<T extends { id: string }>(
  items: T[],
  take: number
): { page: T[]; hasNextPage: boolean; nextCursor: string | null } {
  const hasNextPage = items.length > take;
  const page = hasNextPage ? items.slice(0, take) : items;
  const nextCursor = hasNextPage ? (page[page.length - 1]?.id ?? null) : null;
  return { page, hasNextPage, nextCursor };
}

export const PARTICIPANT_GROUP_VIEW_PAGE_SIZE = 20;

export type DirectoryCursorPagination = {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  nextCursor: string | null;
  prevCursor: string | null;
};

export function buildDirectoryCursorQuery(
  take: number,
  cursor?: string,
  before?: string
): {
  take: number;
  orderBy: { id: "asc" } | { id: "desc" };
  cursor?: { id: string };
  skip?: number;
  reverse: boolean;
} {
  if (before) {
    return {
      take: take + 1,
      orderBy: { id: "desc" },
      cursor: { id: before },
      skip: 1,
      reverse: true,
    };
  }
  return {
    take: take + 1,
    orderBy: { id: "asc" },
    ...(cursor ? { cursor: { id: cursor }, skip: 1 } : {}),
    reverse: false,
  };
}

/** Forward/reverse cursor window built on directoryPageMeta's take+1 rule. */
export function directoryCursorPageMeta<T extends { id: string }>(
  items: T[],
  take: number,
  opts: { cursor?: string; before?: string; reverse?: boolean } = {}
): { page: T[]; pagination: DirectoryCursorPagination } {
  const { cursor, before, reverse } = opts;
  const fetchedExtra = items.length > take;
  const window = fetchedExtra ? items.slice(0, take) : items;

  if (reverse && before) {
    const page = [...window].reverse();
    return {
      page,
      pagination: {
        hasNextPage: true,
        hasPreviousPage: fetchedExtra,
        nextCursor: page[page.length - 1]?.id ?? null,
        prevCursor: fetchedExtra ? (page[0]?.id ?? null) : null,
      },
    };
  }

  const hasPreviousPage = Boolean(cursor);
  return {
    page: window,
    pagination: {
      hasNextPage: fetchedExtra,
      hasPreviousPage,
      nextCursor: fetchedExtra ? (window[window.length - 1]?.id ?? null) : null,
      prevCursor: hasPreviousPage ? (window[0]?.id ?? null) : null,
    },
  };
}

export const EMPTY_DIRECTORY_CURSOR_PAGINATION: DirectoryCursorPagination = {
  hasNextPage: false,
  hasPreviousPage: false,
  nextCursor: null,
  prevCursor: null,
};

export function applyDirectoryPages<
  T extends {
    id: string;
    type: string;
    participants: { id: string }[];
    exhibitors: { id: string }[];
    sponsors: { id: string }[];
  },
>(
  groups: T[],
  take: number
): { groups: T[]; groupsMeta: Record<string, DirectoryGroupMeta> } {
  const groupsMeta: Record<string, DirectoryGroupMeta> = {};
  const paged = groups.map((group) => {
    const key = directoryRelationKey(group.type);
    const { page, hasNextPage, nextCursor } = directoryPageMeta(
      group[key],
      take
    );
    groupsMeta[group.id] = { hasNextPage, nextCursor };
    return { ...group, [key]: page } as T;
  });
  return { groups: paged, groupsMeta };
}
